import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/lib/api";
import { useSiteAssets } from "@/lib/siteAssets";
import { imageUrl } from "@/components/ImageUpload";
import { Menu, X, Inbox as InboxIcon } from "lucide-react";

export const Header = () => {
    const { user, signOut } = useAuth();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    const assets = useSiteAssets();
    const BRAND_LOGO = imageUrl(assets.brand_logo);

    useEffect(() => {
        let cancel = false;
        const poll = async () => {
            if (!user || user.role === "admin") { setUnread(0); return; }
            try {
                const res = await api.get("/messages/unread-count");
                if (!cancel) setUnread(res.data.unread || 0);
            } catch (err) { console.debug("Shell: optional load failed", err); }
        };
        poll();
        const t = setInterval(poll, 15000);
        return () => { cancel = true; clearInterval(t); };
    }, [user]);

    const dashHref = user
        ? user.role === "admin" ? "/admin"
        : user.role === "host_company" ? "/host"
        : "/vendor"
        : "/login";

    const linkCls = ({ isActive }) =>
        `text-sm font-medium transition-colors ${isActive ? "text-brand-forest" : "text-brand-ink/70 hover:text-brand-forest"}`;

    return (
        <header className="fixed top-0 inset-x-0 z-50 glass-header" data-testid="site-header">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5 group">
                    <span className="relative inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg bg-brand-bone ring-1 ring-brand-line">
                        <img src={BRAND_LOGO} alt="HealthFairX" className="h-9 w-9 object-contain" />
                    </span>
                    <span className="font-heading text-lg font-extrabold tracking-tight">HealthFair<span className="text-brand-coral">X</span></span>
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    <NavLink to="/marketplace" className={linkCls} data-testid="nav-events">Events</NavLink>
                    <NavLink to="/vendors" className={linkCls} data-testid="nav-vendors">Vendors</NavLink>
                    <NavLink to="/how-it-works" className={linkCls} data-testid="nav-how">How it works</NavLink>
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    {user ? (
                        <>
                            {(user.role === "host_company" || user.role === "vendor") && (
                                <Link to="/inbox" data-testid="nav-inbox" className="relative inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-brand-bone text-brand-ink transition-colors" aria-label="Inbox">
                                    <InboxIcon size={18} />
                                    {unread > 0 && (
                                        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-coral text-white text-[10px] font-bold inline-flex items-center justify-center" data-testid="nav-inbox-badge">
                                            {unread > 99 ? "99+" : unread}
                                        </span>
                                    )}
                                </Link>
                            )}
                            <Link to={dashHref} data-testid="nav-dashboard" className="text-sm font-semibold text-brand-forest hover:text-brand-forest-dark">Dashboard</Link>
                            <button data-testid="nav-signout" onClick={() => { signOut(); nav("/"); }} className="pill px-4 py-2 text-sm font-semibold border border-brand-line text-brand-ink hover:bg-brand-bone">Sign out</button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" data-testid="nav-login" className="text-sm font-semibold text-brand-ink hover:text-brand-forest">Log in</Link>
                            <Link to="/register" data-testid="nav-register" className="pill btn-coral px-5 py-2 text-sm font-semibold">Get started</Link>
                        </>
                    )}
                </div>

                <button data-testid="nav-mobile-toggle" className="md:hidden p-2" onClick={() => setOpen(v => !v)} aria-label="menu">
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>
            {open && (
                <div className="md:hidden border-t border-brand-line/60 bg-white" data-testid="mobile-menu">
                    <div className="px-6 py-4 flex flex-col gap-3">
                        <NavLink to="/marketplace" onClick={() => setOpen(false)} className={linkCls}>Events</NavLink>
                        <NavLink to="/vendors" onClick={() => setOpen(false)} className={linkCls}>Vendors</NavLink>
                        <NavLink to="/how-it-works" onClick={() => setOpen(false)} className={linkCls}>How it works</NavLink>
                        <div className="h-px bg-brand-line my-2" />
                        {user ? (
                            <>
                                {(user.role === "host_company" || user.role === "vendor") && (
                                    <Link to="/inbox" onClick={() => setOpen(false)} className="font-semibold flex items-center justify-between">
                                        <span>Inbox</span>
                                        {unread > 0 && <span className="px-2 py-0.5 rounded-full bg-brand-coral text-white text-[10px] font-bold">{unread > 99 ? "99+" : unread}</span>}
                                    </Link>
                                )}
                                <Link to={dashHref} onClick={() => setOpen(false)} className="font-semibold text-brand-forest">Dashboard</Link>
                                <button onClick={() => { signOut(); setOpen(false); nav("/"); }} className="text-left font-semibold">Sign out</button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" onClick={() => setOpen(false)} className="font-semibold">Log in</Link>
                                <Link to="/register" onClick={() => setOpen(false)} className="pill btn-coral inline-flex w-fit px-5 py-2 font-semibold">Get started</Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
};

export const Footer = () => {
    const assets = useSiteAssets();
    const BRAND_LOGO = imageUrl(assets.brand_logo);
    return (
    <footer className="border-t border-brand-line bg-white mt-24" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
            <div className="col-span-2">
                <div className="flex items-center gap-2.5 mb-3">
                    <span className="inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg bg-brand-bone ring-1 ring-brand-line">
                        <img src={BRAND_LOGO} alt="HealthFairX" className="h-8 w-8 object-contain" />
                    </span>
                    <span className="font-heading font-extrabold">HealthFair<span className="text-brand-coral">X</span></span>
                </div>
                <p className="text-sm text-brand-mute max-w-md">The marketplace that helps companies run remarkable employee health fairs. Free for hosts. Curated vendors, zero noise.</p>
            </div>
            <div>
                <div className="label-base">Platform</div>
                <ul className="space-y-2 text-sm">
                    <li><Link to="/marketplace" className="hover:text-brand-forest">Events</Link></li>
                    <li><Link to="/vendors" className="hover:text-brand-forest">Vendors</Link></li>
                    <li><Link to="/health-fairs" className="hover:text-brand-forest" data-testid="footer-health-fairs-link">Health fairs by city</Link></li>
                    <li><Link to="/how-it-works" className="hover:text-brand-forest">How it works</Link></li>
                </ul>
            </div>
            <div>
                <div className="label-base">Account</div>
                <ul className="space-y-2 text-sm">
                    <li><Link to="/register" className="hover:text-brand-forest">Sign up</Link></li>
                    <li><Link to="/login" className="hover:text-brand-forest">Log in</Link></li>
                </ul>
            </div>
            <div>
                <div className="label-base">Legal</div>
                <ul className="space-y-2 text-sm">
                    <li><Link to="/privacy" className="hover:text-brand-forest" data-testid="footer-privacy-link">Privacy Policy</Link></li>
                    <li><Link to="/terms" className="hover:text-brand-forest" data-testid="footer-terms-link">Terms of Service</Link></li>
                    <li><Link to="/refund-policy" className="hover:text-brand-forest" data-testid="footer-refund-link">Refund policy</Link></li>
                    <li><Link to="/sms-opt-in" className="hover:text-brand-forest" data-testid="footer-sms-link">Text alerts (SMS opt-in)</Link></li>
                    <li><a href="/sitemap.xml" className="hover:text-brand-forest" data-testid="footer-sitemap-link">Sitemap</a></li>
                </ul>
            </div>
        </div>
        <div className="border-t border-brand-line px-6 lg:px-10 py-5 text-xs text-brand-mute max-w-7xl mx-auto">© {new Date().getFullYear()} HealthFairX. All rights reserved.</div>
    </footer>
    );
};

// Persistent "Please verify your email" banner. Shown once a logged-in user
// is missing email_verified. Self-dismisses for the session; nags again next
// login if still unverified.
const VerifyBanner = () => {
    const { user, refreshUser } = useAuth();
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("hfx_verify_dismiss") === "1");
    if (!user || user.email_verified || dismissed || user.role === "admin" || user.is_test_user) return null;
    const resend = async () => {
        setBusy(true);
        try {
            await api.post("/auth/send-verification");
            setSent(true);
        } catch (err) { console.warn("send-verification failed", err); }
        finally { setBusy(false); }
    };
    const dismiss = () => {
        sessionStorage.setItem("hfx_verify_dismiss", "1");
        setDismissed(true);
    };
    const ping = async () => { await refreshUser(); };
    return (
        <div className="fixed top-16 inset-x-0 z-30 bg-brand-coral/10 border-b border-brand-coral/30 text-sm" data-testid="verify-email-banner">
            <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-brand-ink">
                    <strong>Verify your email</strong> to apply to events, host fairs, and send notifications. Check {user.email}.
                </div>
                <div className="flex items-center gap-2">
                    {sent ? (
                        <span className="text-xs text-emerald-700 font-semibold" data-testid="verify-resend-sent">Sent! Check your inbox.</span>
                    ) : (
                        <button data-testid="verify-resend-btn" disabled={busy} onClick={resend} className="pill px-3 py-1.5 text-xs font-semibold bg-white border border-brand-line hover:bg-brand-bone disabled:opacity-50">{busy ? "Sending…" : "Resend email"}</button>
                    )}
                    <button onClick={ping} className="pill px-3 py-1.5 text-xs font-semibold bg-white border border-brand-line hover:bg-brand-bone">I verified, refresh</button>
                    <button onClick={dismiss} aria-label="Dismiss" className="text-brand-mute hover:text-brand-ink"><X size={14} /></button>
                </div>
            </div>
        </div>
    );
};

export const Shell = ({ children }) => (
    <div className="min-h-screen flex flex-col">
        <Header />
        <VerifyBanner />
        <main className="flex-1 pt-16">{children}</main>
        <Footer />
    </div>
);
